"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import type { PublicClientStory } from "@/lib/testimonial-roster";
import type { CaseStudy, WrittenCaseStudy } from "./case-studies-data";
import { ModuleStyle } from "@/modules/module-style"

export type { CaseStudy } from "./case-studies-data";

/**
 * The two-up polaroid rows (owner, 2026-08-21: "I think I really like number
 * three which is two up… This should be the design that we adopt on every
 * single page that has the case studies. So propagate that outwards").
 *
 * It replaces the horizontal rail he killed. Two studies per desktop row, one
 * per row on a phone, each a tilted polaroid with the name and age captioned
 * under it. A study with no approved portrait renders the age numeral and the
 * name alone rather than a placeholder (MEDIA.md LAW 3), so a missing photo
 * never reads as a broken card, and the row fills in the day the portrait
 * lands.
 */
// Design notes for CSS below. They live here, outside the template literal,
// so the minifier drops them and they never reach the public client JS chunk.
//
// Above `.ca-cases h2`:
//   Weight 800, the decided Jakarta system. This heading carried
//   font-weight:500 with letter-spacing -.045em, which is the PRE-Jakarta Inter
//   heading treatment the rollout replaced: every other section heading on all
//   three pages that mount this block is 800, so at 3.1rem the old 500 read as a
//   different typeface entirely (owner, Aug 21, on /online-dating-mentorship:
//   "pretty sure these are using some of the wrong fonts"). The family was
//   always right; only the weight was stale.
//
// Above `.ca-cases-dialog .ca-dialog-body h3`:
//   The dialog headings carry the same Jakarta weight as every other heading
//   on the three pages that mount this block. Both were left behind when the
//   card heading moved to 800: the title sat at 600, and the section headings
//   inside the story declared no weight at all, so they fell through to the
//   global 500 that predates Jakarta. That is what read as the wrong font.
//
// Above `@media (min-width:760px)`:
//   Two studies per row from the mock's own breakpoint. Below it the grid is a
//   single column, so a phone reads one study at a time with no sideways gesture
//   and no half-cut card.
//
// Above `.ca-cases .ca-action.tap-target`:
//   The story button's invisible 44px tap box (.tap-target) is for the
//   single-column phone layout. From here up the larger polaroid's shadow
//   reaches the button's first letters, and a positioned button would paint
//   over that shadow, so the box is switched off and nothing changes.
const CSS = `
.ca-cases{padding:96px 0;background:var(--color-paper-deep);border-top:1px solid var(--color-rule)}

.ca-cases h2{text-align:center;font-size:clamp(2.1rem,4.6vw,3.1rem);line-height:1.1;
  letter-spacing:-.04em;font-weight:800;margin:0 0 48px;color:var(--color-ink);padding:0 32px}
.ca-cases h2 span{display:block}
.ca-cases .ca-rail{display:grid;grid-template-columns:1fr;gap:22px;padding:0 26px;
  max-width:1000px;margin:0 auto}
.ca-cases .ca-case{display:flex;align-items:flex-start;gap:16px;padding:20px 0;
  border-top:1px solid var(--color-rule)}
.ca-cases .ca-case figure{margin:0;flex:0 0 auto;background:var(--color-paper);
  padding:6px 6px 20px;width:118px;transform:rotate(-3deg);box-shadow:0 8px 22px rgba(0,0,0,.15)}
.ca-cases .ca-case:nth-child(even) figure{transform:rotate(3deg)}
.ca-cases .ca-case img{width:100%;aspect-ratio:3/4;object-fit:cover;display:block}
.ca-cases .ca-case figcaption{margin:8px 2px 0;font-size:10px;font-weight:800;letter-spacing:.1em;
  text-transform:uppercase;color:var(--color-vermilion);text-align:center}
.ca-cases .ca-mark{flex:0 0 auto}
.ca-cases .ca-mark .ca-num{margin:0;font-size:2.5rem;font-weight:800;letter-spacing:-.05em;
  line-height:.85;color:var(--color-ink)}
.ca-cases .ca-mark .ca-nm{margin:5px 0 0;font-size:10.5px;font-weight:800;letter-spacing:.14em;
  text-transform:uppercase;color:var(--color-vermilion)}
.ca-cases .ca-case .ca-body{display:flex;flex-direction:column;align-items:flex-start;min-width:0}
.ca-cases .ca-case h3{margin:0;font-size:1.1rem;font-weight:800;letter-spacing:-.03em;
  line-height:1.22;color:var(--color-ink);max-width:26ch}
.ca-cases .ca-case .ca-body p{margin:9px 0 0;font-size:13.6px;font-weight:500;letter-spacing:-.004em;
  line-height:1.55;color:var(--color-ink-muted);max-width:40ch}
.ca-cases .ca-action{margin-top:11px;border:0;background:none;padding:0 0 2px;font:inherit;
  font-size:12.6px;font-weight:800;line-height:1.2;color:var(--color-vermilion);cursor:pointer;
  border-bottom:2px solid var(--color-vermilion)}
.ca-cases .ca-action:hover{color:var(--color-oxblood);border-bottom-color:var(--color-oxblood)}
.ca-cases .ca-action:focus-visible,.ca-cases .ca-close:focus-visible{outline:2px solid var(--color-vermilion);outline-offset:3px}
.ca-cases-dialog{width:min(760px,calc(100vw - 32px));max-height:84vh;padding:0;
  border:1px solid var(--color-rule-strong);border-radius:20px;background:var(--color-paper);
  color:var(--color-ink);box-shadow:var(--glass-shadow-3)}
.ca-cases-dialog::backdrop{background:rgba(21,21,21,.66);backdrop-filter:blur(4px)}
.ca-cases-dialog .ca-dialog-head{position:sticky;top:0;z-index:1;display:flex;gap:20px;
  align-items:flex-start;justify-content:space-between;padding:24px 26px 18px;
  background:var(--color-paper);border-bottom:1px solid var(--color-rule)}
.ca-cases-dialog .ca-dialog-head h2{margin:0;padding:0;text-align:left;font-size:clamp(1.65rem,3vw,2.25rem);
  line-height:1.12;letter-spacing:-.035em;font-weight:800;color:var(--color-ink)}
.ca-cases-dialog .ca-close{flex:0 0 auto;border:1px solid var(--color-rule-strong);border-radius:999px;
  background:var(--color-paper);color:var(--color-ink);font:inherit;font-size:12px;font-weight:650;
  line-height:1;padding:9px 12px;cursor:pointer}
.ca-cases-dialog .ca-dialog-body{padding:24px 26px 32px}
.ca-cases-dialog .ca-dialog-body p,.ca-cases-dialog .ca-dialog-body li{font-size:16px;
  line-height:1.7;color:var(--color-ink-muted);letter-spacing:0;font-weight:400}
.ca-cases-dialog .ca-dialog-body p{margin:0 0 18px}

.ca-cases-dialog .ca-dialog-body h3{margin:30px 0 12px;font-size:19px;line-height:1.35;
  letter-spacing:-.02em;font-weight:800;color:var(--color-ink)}
.ca-cases-dialog .ca-dialog-body ol{margin:0 0 20px;padding-left:26px}
.ca-cases-dialog .ca-dialog-body li{padding-left:4px;margin:0 0 7px}
.ca-cases-dialog blockquote{margin:0 0 24px;padding:14px 17px;border-left:3px solid var(--color-vermilion);
  color:var(--color-ink);font-size:17px;font-weight:600;line-height:1.55}
.ca-cases-dialog blockquote.ca-closing-quote{margin:24px 0 0}

@media (min-width:760px){
  .ca-cases .ca-rail{grid-template-columns:1fr 1fr;column-gap:56px;row-gap:10px;padding:0 32px}
  .ca-cases .ca-case{padding:26px 0;gap:22px}
  .ca-cases .ca-case figure{width:128px}
  .ca-cases .ca-case h3{font-size:1.16rem}
  .ca-cases .ca-case .ca-body p{font-size:14px}
  
  .ca-cases .ca-action.tap-target{position:static}
  .ca-cases .ca-action.tap-target::after{content:none}
}
@media (max-width:550px){
  .ca-cases{padding:64px 0}
  .ca-cases h2{text-align:left;margin-bottom:36px;padding:0 26px}
  .ca-cases-dialog .ca-dialog-head{padding:20px 20px 16px}
  .ca-cases-dialog .ca-dialog-body{padding:20px 20px 26px}
}
`;

/**
 * The client-story frame of the same section, the /mentorship section he
 * answered "Add it" to: under the shared heading, each client's story
 * in his own words as three beats down a line, an empty ring for where he
 * started, a half-filled ring for what he worked on, and a crimson check for
 * where he is now. Static, the render's finished frame. Shipped only with the
 * section it styles.
 */
const STORIES_CSS = `
.ca-cases.ca-stories{padding-bottom:104px}
.ca-stories .ca-sgrid{display:grid;grid-template-columns:1fr;gap:10px;max-width:1000px;margin:0 auto;padding:0 26px}
.ca-stories .ca-story{border-top:1px solid var(--color-rule);padding:28px 0 30px}
.ca-stories .ca-shead{display:flex;align-items:baseline;flex-wrap:wrap;gap:4px 12px;margin:0 0 22px}
.ca-stories .ca-sname{margin:0;font-size:22px;font-weight:800;letter-spacing:-.035em;line-height:1.2;color:var(--color-ink)}
.ca-stories .ca-splace{margin:0;font-size:14px;font-weight:600;color:#8A8A8A}
.ca-stories .ca-beats{list-style:none;margin:0;padding:0}
.ca-stories .ca-beat{position:relative;display:grid;grid-template-columns:18px 1fr;column-gap:18px;padding:0 0 20px}
.ca-stories .ca-beat:last-child{padding-bottom:0}
.ca-stories .ca-beat::after{content:"";position:absolute;left:8px;top:24px;bottom:2px;width:2px;background:var(--color-rule-strong)}
.ca-stories .ca-beat:last-child::after{display:none}
.ca-stories .ca-node{display:block;width:18px;height:18px;margin-top:2px}
.ca-stories .ca-node svg{display:block}
.ca-stories .ca-lbl{margin:0 0 5px;font-size:13px;font-weight:700;letter-spacing:.005em;color:#8A8A8A;line-height:1.5}
.ca-stories .ca-txt{margin:0;font-size:16px;line-height:1.62;color:var(--color-ink-muted);max-width:42ch;text-wrap:pretty}
.ca-stories .ca-beat.ca-now .ca-lbl{color:var(--color-ink)}
.ca-stories .ca-beat.ca-now .ca-txt{color:var(--color-ink);font-weight:600}
@media (min-width:760px){
  .ca-stories .ca-sgrid{grid-template-columns:1fr 1fr;column-gap:56px;row-gap:10px;padding:0 32px}
  .ca-stories .ca-story{padding:30px 0 34px}
}
@media (max-width:550px){
  .ca-cases.ca-stories{padding:64px 0 72px}
  .ca-stories .ca-txt{font-size:15.5px}
  .ca-stories .ca-sname{font-size:20px}
}
`;

/** The three beat marks: where he started, what he worked on, where he is now. */
const STARTED_NODE = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <circle cx="9" cy="9" r="7.5" fill="#fff" stroke="#8A8A8A" strokeWidth="2" />
  </svg>
);
const WORKED_NODE = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <circle cx="9" cy="9" r="7.5" fill="#fff" stroke="#151515" strokeWidth="2" />
    <circle cx="9" cy="9" r="7.5" fill="#151515" style={{ clipPath: "inset(0 50% 0 0)" }} />
  </svg>
);
const NOW_NODE = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <circle cx="9" cy="9" r="9" fill="#A41034" />
    <path d="M5.2 9.3 L7.9 12 L12.9 6.2" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ClientStoryRows({ stories }: { stories: PublicClientStory[] }) {
  return (
    <div className="ca-sgrid">
      {stories.map((story) => {
        // One quote split over three beats: the mark opens on the first beat
        // and closes on the last, as the render sets it.
        const beats = [
          { key: "started", label: "Where he started", node: STARTED_NODE, text: `“${story.started}` },
          { key: "worked", label: "What we worked on", node: WORKED_NODE, text: story.workedOn },
          { key: "now", label: "Where he is now", node: NOW_NODE, text: `${story.now}”` },
        ];
        return (
          <article className="ca-story" key={story.name}>
            <header className="ca-shead">
              <p className="ca-sname">{story.name}</p>
              <p className="ca-splace">{story.place}</p>
            </header>
            <ol className="ca-beats">
              {beats.map((beat) => (
                <li className={beat.key === "now" ? "ca-beat ca-now" : "ca-beat"} key={beat.key}>
                  <span className="ca-node">{beat.node}</span>
                  <div>
                    <p className="ca-lbl">{beat.label}</p>
                    <p className="ca-txt">{beat.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </article>
        );
      })}
    </div>
  );
}

/**
 * The two lines of prose under each headline, taken from the study's OWN
 * opening paragraph rather than written for the card.
 *
 * The card needs a second block of text and the data has no teaser field, so
 * the choice is to invent copy or to reuse his. It reuses his: the first
 * sentences of the opening paragraph, stopping on a sentence boundary once
 * there is enough to read. Nothing is paraphrased, so a card can never claim
 * something the study does not say, and editing the study edits the card.
 */
function studyTeaser(study: WrittenCaseStudy): string {
  const opening = study.blocks.find((block) => block.kind === "paragraph");
  if (!opening) return "";
  // Split on sentence ends, keeping the punctuation with its sentence.
  const sentences = opening.text.match(/[^.!?]+[.!?]+(\s|$)/g) ?? [opening.text];
  let out = "";
  for (const sentence of sentences) {
    if (out && out.length + sentence.length > 200) break;
    out += sentence;
    if (out.trim().length >= 90) break;
  }
  return out.trim() || opening.text;
}

function CaseStudyBody({ study }: { study: WrittenCaseStudy }) {
  return (
    <>
      {study.openingQuote ? (
        <blockquote>{study.openingQuote}</blockquote>
      ) : null}
      {study.blocks.map((block, index) => {
        if (block.kind === "heading") {
          return <h3 key={`${block.text}-${index}`}>{block.text}</h3>;
        }
        if (block.kind === "ordered-list") {
          return (
            <ol key={`list-${index}`}>
              {block.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          );
        }
        return <p key={`${block.text}-${index}`}>{block.text}</p>;
      })}
      {study.closingQuote ? (
        <blockquote className="ca-closing-quote">
          {study.closingQuote}
        </blockquote>
      ) : null}
    </>
  );
}

export function CaseStudies({
  studies = [],
  heading = ["Plenty of", "Case Studies..."],
  stories,
}: {
  /** The page's own study set; every mount passes its own list, so this
   *  client component never imports the shared data file (it would ship the
   *  whole file to a page that shows none of it). */
  studies?: CaseStudy[];
  /** The heading's lines, one block span each. */
  heading?: readonly string[];
  /** Client stories in their own words (/mentorship). When given, the
   *  section shows them as three-beat stories under the same heading, in
   *  place of the study rows. */
  stories?: PublicClientStory[];
} = {}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState<CaseStudy | null>(null);

  useEffect(() => {
    if (selected && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    }
  }, [selected]);

  const headingEl = (
    <h2>
      {heading.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </h2>
  );

  if (stories) {
    return (
      <section className="ca-cases ca-stories">
        <ModuleStyle css={CSS + STORIES_CSS} />
        {headingEl}
        <ClientStoryRows stories={stories} />
      </section>
    );
  }

  return (
    <section className="ca-cases">
      <ModuleStyle css={CSS} />
      {headingEl}
      <div className="ca-rail">
        {studies.map((caseStudy) => (
          <article className={caseStudy.photo ? "ca-case" : "ca-case ca-nophoto"} key={caseStudy.name}>
            {/* A subject with no approved representative portrait renders the
                age numeral and the name instead of a figure (MEDIA.md LAW 3 —
                never a placeholder, never a borrowed face). The row still
                reads as a row, and the polaroid appears the day the portrait
                lands. */}
            {caseStudy.photo ? (
              <figure>
                {/* The polaroid is 118px wide on a phone and 128px from 760px
                    up, which is what `sizes` declares. */}
                <Image
                  src={caseStudy.photo.src}
                  alt={caseStudy.photo.alt}
                  width={caseStudy.photo.width}
                  height={caseStudy.photo.height}
                  sizes="128px"
                  style={{ objectPosition: caseStudy.photo.objectPosition }}
                />
                <figcaption>
                  {caseStudy.name}, {caseStudy.age}
                </figcaption>
              </figure>
            ) : (
              <div className="ca-mark">
                <p className="ca-num">{caseStudy.age}</p>
                <p className="ca-nm">{caseStudy.name}</p>
              </div>
            )}
            <div className="ca-body">
              <h3>{caseStudy.body}</h3>
              {caseStudy.fullCaseStudy ? (
                <>
                  <p>{caseStudy.teaser ?? studyTeaser(caseStudy.fullCaseStudy)}</p>
                  <button
                    className="ca-action tap-target"
                    type="button"
                    onClick={() => setSelected(caseStudy)}
                  >
                    {/* The space is explicit: JSX drops a leading space in a
                        text node beside an expression (web/AGENTS.md). */}
                    {caseStudy.actionLabel ?? "Read Full Case Study"}{" ›"}
                  </button>
                </>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <dialog
        className="ca-cases-dialog"
        ref={dialogRef}
        aria-labelledby="ca-case-dialog-title"
        onClose={() => setSelected(null)}
        onClick={(event) => {
          if (event.target === event.currentTarget) event.currentTarget.close();
        }}
      >
        {selected?.fullCaseStudy ? (
          <>
            <div className="ca-dialog-head">
              <h2 id="ca-case-dialog-title">{selected.fullCaseStudy.title}</h2>
              <button
                className="ca-close"
                type="button"
                aria-label="Close case study"
                onClick={() => dialogRef.current?.close()}
              >
                Close
              </button>
            </div>
            <div className="ca-dialog-body">
              <CaseStudyBody study={selected.fullCaseStudy} />
            </div>
          </>
        ) : null}
      </dialog>
    </section>
  );
}
